import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import icon_account from "./../img/icon/account.svg";
import icon_gear from "./../img/icon/gear.svg";
import icon_clock from "./../img/icon/clock.svg";
import icon_document from "./../img/icon/document.svg";
import icon_cloud_database from "./../img/icon/cloud-database.svg";
import icon_cloud_server from "./../img/icon/server-cloud.svg";
import icon_search from "./../img/icon/search.svg";
import icon_graph from "./../img/icon/graph.svg";
import {
    ACCOUNT_SETTING_ROUTE,
    DATA_MANAGEMENT_ROUTE,
    LICENSE_ISSUE_HISTORY_ROUTE,
    LICENSE_ISSUE_START_ROUTE,
    MANAGEMENT_LICENSE_STATISTICS,
    MANAGEMENT_USER_SEARCH,
    PAYMENT_HISTORY_ROUTE,
    USER_LICENSE_REFERENCE_ROUTE,
    USER_LICENSE_ROUTE,
} from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { ScrollToTop } from "../components/ScrollToTop";
import { Helmet } from "react-helmet";

/**
 * マイページ画面
 * @returns JSX.Element
 */
const Mypage = () => {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const [userType, setUserType] = useState<number | null>(null);

    // コンテキスト情報の取得
    useEffect(() => {
        setUserType(Number(user?.attributes["custom:user_type"]) ?? null);
    }, [user]);

    return (
        <div className="flex min-h-screen flex-col bg-slate-50">
            <Helmet title={t("Mypage.meta.title")} />
            <ScrollToTop />
            <DFNavbar bottomPadding={true} />
            <div className="mx-auto flex-grow items-center justify-center px-6 py-8 pt-20">
                {/* My メニュー */}
                <div className="container mx-auto px-5 py-5">
                    <div className="flex items-center py-4">
                        <div className="h-2 flex-grow bg-lime-800 opacity-70"></div>
                        <span className="flex-shrink px-4 text-2xl text-lime-800">{t("Mypage.myMenu")}</span>
                        <div className="h-2 flex-grow bg-lime-800 opacity-70"></div>
                    </div>

                    <div className="-m-4 flex flex-wrap">
                        {/* アカウント情報設定 */}
                        <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                            <Link to={ACCOUNT_SETTING_ROUTE}>
                                <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200 ">
                                    <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                        <img src={icon_gear} alt="account setting" />
                                    </div>
                                    <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                        {t("Mypage.accountSettingTitle")}
                                    </h2>
                                    <p className="text-base leading-relaxed">{t("Mypage.accountSettingText")}</p>
                                </button>
                            </Link>
                        </div>

                        {/* ユーザー・ライセンス管理 */}
                        {userType != null && [0, 1, 3].includes(userType) ? (
                            // マスターユーザー、管理ユーザー、マネージャーの場合 => 編集可能な管理画面
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to={USER_LICENSE_ROUTE}>
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_account} alt="User license control" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.userLicenseTitle")}
                                        </h2>
                                        <p className="text-base leading-relaxed">{t("Mypage.userLicenseText")}</p>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            // それ以外の場合 => 参照専用の画面
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to={USER_LICENSE_REFERENCE_ROUTE}>
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_account} alt="User license reference" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.userLicenseReferenceTitle")}
                                        </h2>
                                        <p className="text-base leading-relaxed">
                                            {t("Mypage.userLicenseReferenceText")}
                                        </p>
                                    </button>
                                </Link>
                            </div>
                        )}

                        {/* 購入履歴管理 */}
                        {userType != null && [0, 1, 3].includes(userType) ? (
                            // マスターユーザー、管理ユーザー、マネージャーの場合
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to={PAYMENT_HISTORY_ROUTE}>
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_clock} alt="Payment history" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.paymentHistoryTitle")}
                                        </h2>
                                        <p className="text-base leading-relaxed">{t("Mypage.paymentHistoryText")}</p>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            // それ以外の場合
                            <></>
                        )}

                        {/* データ管理 */}
                        <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                            <Link to={DATA_MANAGEMENT_ROUTE}>
                                <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                    <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                        <img src={icon_cloud_database} alt="cloud database" />
                                    </div>
                                    <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                        {t("Mypage.dataManagementTitle")}
                                    </h2>
                                    <p className="text-base leading-relaxed">{t("Mypage.dataManagementText")}</p>
                                </button>
                            </Link>
                        </div>

                        {/* DFアカウント検索 */}
                        {userType != null && [0].includes(userType) ? (
                            // マスターユーザーの場合
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to={MANAGEMENT_USER_SEARCH}>
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_search} alt="user search" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.managementUserSearchTitle")}
                                        </h2>
                                        <p className="text-base leading-relaxed">
                                            {t("Mypage.managementUserSearchText")}
                                        </p>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            // それ以外の場合
                            <></>
                        )}

                        {/* ライセンス統計 */}
                        {userType != null && [0].includes(userType) ? (
                            // マスターユーザーの場合
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to={MANAGEMENT_LICENSE_STATISTICS}>
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_graph} alt="license statistics" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.managementLicenseStatisticsTitle")}
                                        </h2>
                                        <p className="text-base leading-relaxed">
                                            {t("Mypage.managementLicenseStatisticsText")}
                                        </p>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            // それ以外の場合
                            <></>
                        )}

                        {/* ストレージ容量変更(管理者向け) */}
                        {userType != null && [0].includes(userType) ? (
                            // マスターユーザーの場合
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to="/storage/issue/user/search">
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_cloud_server} alt="Change storage size" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.storageManagementTitle")}
                                        </h2>
                                        <p className="text-base leading-relaxed">{t("Mypage.storageManagementText")}</p>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            // それ以外の場合
                            <></>
                        )}

                        {/* ライセンス発行 */}
                        {/* {userType != null && [0].includes(userType) ? (
                            // マスターユーザーの場合
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to={LICENSE_ISSUE_START_ROUTE}>
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_document} alt="License issue" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.licenseIssueTitle")} *メンテ中
                                        </h2>
                                        <p className="text-base leading-relaxed">{t("Mypage.licenseIssueText")}</p>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            // それ以外の場合
                            <></>
                        )} */}

                        {/* ライセンス発行履歴確認 */}
                        {/* {userType != null && [0, 3].includes(userType) ? (
                            // マスターユーザー、マネージャーの場合
                            <div className="w-full p-4 md:w-1/2 xl:w-1/3">
                                <Link to={LICENSE_ISSUE_HISTORY_ROUTE}>
                                    <button className="flex h-full w-full flex-col justify-start rounded-lg border-2 border-gray-200 bg-white p-6 text-left shadow-md hover:border-gray-400 hover:bg-lime-100 active:bg-lime-200">
                                        <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-900">
                                            <img src={icon_clock} alt="License issue history" />
                                        </div>
                                        <h2 className="title-font mb-2 text-lg font-medium text-gray-900">
                                            {t("Mypage.licenseIssueHistoryTitle")}
                                        </h2>
                                        <p className="text-base leading-relaxed">
                                            {t("Mypage.licenseIssueHistoryText")}
                                        </p>
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            // それ以外の場合
                            <></>
                        )} */}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Mypage;
